import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import api from '../../services/api';

/**
 * DPDPA Consent Modal for Voice/Video Calls.
 *
 * India's Digital Personal Data Protection Act (DPDPA) requires explicit
 * consent before processing biometric data such as voice patterns and
 * facial data during video calls.
 *
 * Props:
 *   open      - whether the dialog is visible
 *   onConsent - called after consent is successfully recorded on backend
 *   onDecline - called when user declines (cannot proceed with call)
 */
const CallConsentModal = ({ open, onConsent, onDecline }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAgree = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.put('/profile/call-consent');
      onConsent();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onDecline}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#FFFFFF',
          border: '1px solid rgba(218,165,32,0.2)',
          color: '#2C1810',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2C1810' }}>
        <GavelIcon sx={{ color: '#f9d71c' }} />
        Consent for Voice/Video Calls
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ color: '#2C1810', mb: 2 }}>
          To provide you with voice and video consultation services, we need your
          explicit consent to process audio and visual data during the call, in
          accordance with India's Digital Personal Data Protection Act (DPDPA).
        </Typography>

        <Box sx={{
          background: '#FFFDF7',
          borderRadius: 2,
          p: 2,
          mb: 2,
          border: '1px solid rgba(218,165,32,0.1)',
        }}>
          <Typography variant="subtitle2" sx={{ color: '#DAA520', mb: 1 }}>
            Key Points:
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#6B5744' }}>
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Your voice and video data will be processed in real-time solely for
                the purpose of the consultation session.
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                No audio or video recordings are made or stored beyond the live session.
              </li>
              <li>
                Data is not retained, shared, or used for any purpose after the session ends.
              </li>
            </ul>
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: '#9B8B7A' }}>
          By clicking "I Agree", you provide your explicit consent for processing
          voice and video data during consultations. You may withdraw consent at
          any time from your profile settings.
        </Typography>

        {error && (
          <Typography variant="body2" sx={{ color: '#f44336', mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onDecline}
          sx={{ color: '#9B8B7A' }}
          disabled={loading}
        >
          Decline
        </Button>
        <Button
          variant="contained"
          onClick={handleAgree}
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #DAA520, #B8860B)',
            '&:hover': { background: 'linear-gradient(135deg, #FFD700, #DAA520)' },
            minWidth: 100,
          }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallConsentModal;
